/**
 * Políticas legales mostradas en registro / modales (ES + EN).
 * Alineadas a Ley 1581 de 2012 (Colombia) y al stack Ecosistema Fit / D28D.
 * Contactos = canales reales de producción (MAIL_FROM + WhatsApp soporte).
 * Actualizado: 2026-08-13 (Antonia).
 */

/** Canal único y operativo (no inventar privacy@ / legal@ sin buzón real). */
export const LEGAL_CONTACT = {
  email: 'gymvirtual@ecosistemafit.app',
  whatsappDisplay: '+57 319 263 5819',
  whatsappDigits: '573192635819',
  company: 'CG CONSULTING S.A.S.',
  nit: '901.314.635-3',
  address: 'Calle 13 # 12-02, Facatativá, Colombia',
  domain: 'ecosistemafit.app',
};

const C = LEGAL_CONTACT;
const WA_URL = `https://wa.me/${C.whatsappDigits}`;

const PRIVACY_ES = `# Política de Privacidad y Tratamiento de Datos Personales

**Plataforma:** Ecosistema Fit / D28D (módulos: Training, Food Plan, Viento Recio cuando estén habilitados)  
**Última actualización:** 13 de agosto de 2026  
**Normativa:** Ley 1581 de 2012, Decreto 1377 de 2013 y normas complementarias (Colombia).

## 1. Responsable y encargado del tratamiento

**Operador tecnológico / responsable de la plataforma:**  
**${C.company}**, NIT **${C.nit}**, domicilio ${C.address}.

**Canales oficiales de habeas data / privacidad (siempre activos):**  
- Correo: **${C.email}**  
- WhatsApp soporte plataforma: **${C.whatsappDisplay}** (${WA_URL})

Cuando te afilias a un **gimnasio, sede o coach** (marca blanca / tenant), ese operador es **responsable** de los datos de su operación comercial (afiliados, cobros locales, acompañamiento). CG CONSULTING actúa como **encargado / operador de la plataforma** para alojar y procesar los datos necesarios para el servicio tecnológico. También puedes ejercer derechos ante el administrador de tu gym/sede; la plataforma responde por el canal oficial arriba.

## 2. Datos que recopilamos

### 2.1 Datos que tú nos entregas
- Identidad y contacto: nombre, correo electrónico, teléfono.
- Credenciales de acceso (la contraseña se almacena cifrada; nunca en texto plano).
- Datos de perfil fitness/nutrición que ingresas de forma voluntaria: peso, altura, fecha de nacimiento, objetivo, preferencias, restricciones, medidas, fotos de progreso (si las cargas).
- Información de pago o solicitud de pago cuando usas pasarelas o métodos configurados (p. ej. Wompi); los datos de tarjeta los procesa el proveedor de pagos; no se almacenan completos en nuestros servidores.

### 2.2 Datos generados por el uso
- Registros de alimentación, entrenamiento, asistencia a clases en vivo, avances de ciclo, evidencias de retos/premiación.
- Logs técnicos mínimos (IP, navegador, fecha/hora) para seguridad, auditoría y soporte.
- Comunicaciones que envíes por soporte (WhatsApp, correo).

### 2.3 Datos sensibles
Algunos datos de salud, composición corporal o creencias (p. ej. contenido espiritual en Viento Recio) pueden ser **datos sensibles**. Solo se tratan con tu **autorización** y para las finalidades aquí descritas. **No estás obligado** a suministrarlos; sin ellos ciertos servicios serán genéricos o limitados.

## 3. Finalidades del tratamiento

1. Crear y administrar tu cuenta y licencias/módulos contratados.
2. Prestar servicios de clases en vivo, entrenamiento, nutrición y (si aplica) acompañamiento espiritual.
3. Permitir que tu coach, admin de gym/sede o soporte autorizado te acompañe dentro de su tenant.
4. Procesar pagos, vigencias, facturación y prevención de fraude.
5. Enviar notificaciones operativas (acceso, vencimientos, clases, seguridad). Comunicaciones comerciales solo con autorización o base legítima aplicable.
6. Mejorar la plataforma de forma agregada/anonimizada.
7. Cumplir obligaciones legales, requerimientos de autoridad y ejercicio de derechos.

**No vendemos tus datos personales. No los compartimos con anunciantes.**

## 4. Bases legales

Según el caso: **autorización** del titular; ejecución de relación contractual/precontractual; cumplimiento de deber legal; interés legítimo compatible (seguridad, prevención de abuso), de conformidad con la Ley 1581 y normas aplicables.

## 5. Con quién compartimos datos (encargados / terceros)

- **Tu gym / sede / coach** (dentro de su alcance).
- **Proveedores técnicos:** hosting VPS (**Contabo** u otro que administremos), bases de datos, correo transaccional, WhatsApp/enlaces de soporte, videollamadas (**Zoom**), pasarela de pagos (**Wompi**).
- **Proveedores de IA** cuando uses funciones de IA (p. ej. **Groq**; opcionalmente Ollama/Anthropic si están configurados): Chat Nico (Food) o generación de textos en Viento Recio. No se autoriza el entrenamiento de modelos propios del operador con tus datos para fines ajenos al servicio.
- **Autoridades** cuando la ley lo exija.

Otros gimnasios o usuarios **no** ven tus datos fuera de tu tenant.

## 6. Transferencias internacionales

Si un proveedor (p. ej. IA o infraestructura) procesa datos fuera de Colombia, aplicaremos medidas razonables de protección y comunicaremos cambios materiales en esta política.

## 7. Seguridad

HTTPS/TLS, contraseñas con hash (bcrypt), control de acceso por roles, secretos fuera del cliente, respaldos de bases de datos y registro de incidentes relevantes. Ningún sistema es 100% seguro; ante incidentes materiales que afecten tus datos, actuaremos conforme a la ley y te informaremos cuando corresponda.

## 8. Tus derechos (titular)

Puedes ejercer, entre otros:
- Conocer, actualizar y rectificar tus datos.
- Solicitar prueba de la autorización.
- Ser informado sobre el uso de tus datos.
- Presentar quejas ante la **Superintendencia de Industria y Comercio (SIC)**.
- Revocar la autorización y/o solicitar la **supresión** cuando proceda.
- Acceder de forma gratuita a tus datos objeto de tratamiento (según periodicidad legal).

**Cómo ejercerlos:** escribe a **${C.email}** o WhatsApp **${C.whatsappDisplay}** indicando tu nombre, correo de la cuenta y la solicitud (consulta, corrección, eliminación, etc.). Responderemos en los plazos de la ley.

## 9. Retención y eliminación

- Mientras tu cuenta esté activa y sea necesario para el servicio.
- Tras baja o terminación: ventana de transición de **30 días** para exportación cuando aplique; luego eliminación o anonimización, salvo retención legal (contable, fiscal, judicial) o respaldos técnicos de rotación mínima.

## 10. Cookies y almacenamiento local

Usamos almacenamiento necesario para sesión (p. ej. JWT) y preferencias (idioma). No usamos cookies publicitarias de terceros como parte del núcleo del servicio.

## 11. Menores de edad

Servicio dirigido a mayores de **18 años**. Menores solo con autorización y supervisión de quien ejerza la patria potestad/representación legal y del gym/operador, cuando el producto lo permita.

## 12. Cambios

Podemos actualizar esta política. Los cambios materiales se comunicarán por la plataforma y/o correo (**${C.email}**). El uso continuado tras la vigencia implica aceptación, sin perjuicio de nuevas autorizaciones cuando la ley lo exija.

## 13. Contacto (completo — sin canales vacíos)

- **Correo:** ${C.email}  
- **WhatsApp:** ${C.whatsappDisplay} — ${WA_URL}  
- **Empresa:** ${C.company} — NIT ${C.nit} — ${C.address}  
- **Dominio:** ${C.domain}

**ACEPTACIÓN:** Al registrarte o usar la plataforma, autorizas el tratamiento de tus datos conforme a esta Política y a la Ley 1581 de 2012, en lo que corresponda.`;

const PRIVACY_EN = `# Privacy and Personal Data Processing Policy

**Platform:** Ecosistema Fit / D28D (modules: Training, Food Plan, Viento Recio when enabled)  
**Last updated:** August 13, 2026  
**Legal reference:** Colombian Law 1581 of 2012 and related regulations.

## 1. Controller and processor

**Platform technology operator:**  
**${C.company}**, Tax ID (NIT) **${C.nit}**, ${C.address}.

**Official privacy / habeas data channels (always active):**  
- Email: **${C.email}**  
- WhatsApp: **${C.whatsappDisplay}** (${WA_URL})

If you join a **gym, location or coach** (white-label / tenant), that operator is **controller** for its commercial operation. CG CONSULTING acts as **processor / platform operator** for hosting and processing needed to provide the technology. You may also contact your gym/sede admin; the platform answers via the official channels above.

## 2. Data we collect

### 2.1 Data you provide
- Identity and contact: name, email, phone.
- Access credentials (password stored hashed; never in plain text).
- Voluntary fitness/nutrition profile: weight, height, date of birth, goals, preferences, restrictions, measurements, progress photos (if uploaded).
- Payment information via configured gateways (e.g. Wompi); full card data is processed by the payment provider and not stored in full on our servers.

### 2.2 Data from use
- Food logs, training, live-class attendance, cycle progress, challenge evidence.
- Minimal technical logs (IP, browser, date/time) for security, audit and support.
- Support messages (WhatsApp, email).

### 2.3 Sensitive data
Health, body-composition or belief-related data (e.g. Viento Recio) may be **sensitive**. Processed only with your **authorization** for the purposes below. You are **not required** to provide them; without them some features are limited.

## 3. Purposes

1. Create and manage your account and licensed modules.
2. Provide live classes, training, nutrition and (if applicable) spiritual accompaniment.
3. Allow your coach, gym/sede admin or authorized support to assist within your tenant.
4. Process payments, validity periods, billing and fraud prevention.
5. Send operational notices (access, expirations, classes, security). Marketing only with authorization or applicable lawful basis.
6. Improve the platform in aggregated/anonymized form.
7. Comply with law, authority requests and data-subject rights.

**We do not sell personal data. We do not share it with advertisers.**

## 4. Legal bases

Authorization; performance of a contract/pre-contract; legal duty; compatible legitimate interest (security, abuse prevention), under Law 1581 as applicable.

## 5. Sharing (processors / third parties)

- Your gym / sede / coach (within scope).
- Technical providers: VPS hosting (**Contabo** or equivalent we manage), databases, transactional email, WhatsApp support links, video (**Zoom**), payments (**Wompi**).
- AI providers when you use AI features (e.g. **Groq**; optionally Ollama/Anthropic): Food Chat Nico or Viento Recio text. No training of the operator’s own models with your data for unrelated purposes.
- Authorities when required by law.

Other gyms or users do **not** see your data outside your tenant.

## 6. International transfers

If a provider processes data outside Colombia, we apply reasonable safeguards and communicate material changes in this policy.

## 7. Security

HTTPS/TLS, bcrypt password hashing, role-based access, secrets kept off the client, database backups and relevant incident logging. No system is 100% secure; for material incidents affecting your data we act under law and notify when required.

## 8. Your rights

Among others: know, update and rectify; request proof of authorization; be informed of use; complain to the **SIC**; revoke authorization and/or request erasure when applicable; free access per legal periodicity.

**How:** email **${C.email}** or WhatsApp **${C.whatsappDisplay}** with your name, account email and request. We respond within legal timeframes.

## 9. Retention and deletion

- While your account is active and needed for the service.
- After closure/termination: **30-day** transition for export when applicable; then deletion or anonymization, except legal retention or minimal backup rotation.

## 10. Cookies and local storage

Session storage (e.g. JWT) and preferences (language). No third-party advertising cookies as part of the core service.

## 11. Minors

Service for **18+**. Minors only with parental/guardian authorization and gym/operator supervision when the product allows it.

## 12. Changes

Material updates will be communicated via the platform and/or **${C.email}**. Continued use after effectiveness means acceptance, without prejudice to new authorizations when required by law.

## 13. Contact (complete — no empty channels)

- **Email:** ${C.email}  
- **WhatsApp:** ${C.whatsappDisplay} — ${WA_URL}  
- **Company:** ${C.company} — NIT ${C.nit} — ${C.address}  
- **Domain:** ${C.domain}

**ACCEPTANCE:** By registering or using the platform you authorize processing under this Policy and Law 1581 of 2012 as applicable.`;

const TERMS_ES = `# Términos y Condiciones de Uso

**Plataforma:** Ecosistema Fit / D28D  
**Última actualización:** 13 de agosto de 2026  
**Operador:** ${C.company}, NIT ${C.nit}

## 1. Aceptación

Al crear una cuenta, iniciar sesión o usar la plataforma aceptas estos Términos y la Política de Privacidad. Si no estás de acuerdo, no uses el servicio.

## 2. Descripción del servicio

Ecosistema Fit / D28D puede incluir, según tu plan y tenant: clases en vivo / calendario / asistencia; Training; Food Plan; Viento Recio; administración multi-tenant gym/sede/coach; pagos y suscripciones. Tu gym o coach puede operar bajo marca blanca.

## 3. Elegibilidad

Mayores de **18 años** (o menores con autorización/supervisión cuando el producto lo permita); capacidad legal; información veraz.

## 4. Cuenta y seguridad

Eres responsable de tus credenciales y de la actividad bajo tu cuenta. No compartas el acceso. Reporta uso no autorizado a **${C.email}** o WhatsApp **${C.whatsappDisplay}**.

## 5. Uso aceptable

Usa el servicio conforme a la ley. Prohibido: acceso no autorizado, scraping abusivo, interferir seguridad, suplantar identidad, acosar, contenido ilegal, ingeniería inversa con fines de competencia desleal.

## 6. Propiedad intelectual

El software, diseños y marcas de la plataforma pertenecen a **${C.company}** o sus licenciantes. Recibes una licencia limitada, **no exclusiva**, revocable y personal según tu plan. Conservas la propiedad del contenido que subes; nos otorgas licencia para alojarlo y mostrarlo para prestar el servicio.

## 7. Pagos

Precios y métodos (Wompi, pago en sede, etc.) dependen del producto/tenant. El no pago puede limitar el acceso. La facturación presencial entre tú y tu gym es entre esas partes.

## 8. Aviso de salud

La información de fitness, nutrición o espiritualidad es **orientación educativa**, no consejo médico. Consulta a un profesional de la salud ante condiciones relevantes. Usas el servicio bajo tu propio riesgo.

## 9. Inteligencia artificial

Algunas funciones (p. ej. Chat Nico en Food o textos en Viento Recio) usan proveedores de IA (p. ej. Groq). Las respuestas pueden ser imperfectas; no sustituyen criterio profesional. Detalle en la Política de Privacidad.

## 10. Servicios de terceros

Zoom, Wompi, WhatsApp, correo y otros terceros se rigen por sus propios términos. No controlamos su disponibilidad continua.

## 11. Disponibilidad y soporte

El servicio se ofrece “tal cual”, con esfuerzo razonable de disponibilidad y soporte en horario de Colombia. No se garantiza uptime ininterrumpido (ver SLA contractual cuando aplique a operadores comerciales). Soporte: **${C.email}** / WhatsApp **${C.whatsappDisplay}**.

## 12. Suspensión y terminación

Podemos suspender o terminar cuentas por incumplimiento, fraude, riesgo de seguridad o requerimiento legal. Puedes solicitar cierre de cuenta; la retención/eliminación de datos se rige por la Política de Privacidad (**30 días** de transición).

## 13. Limitación de responsabilidad

En la máxima medida permitida por la ley colombiana, la responsabilidad agregada del operador por reclamaciones relacionadas con el servicio no excederá lo pagado por ti a la plataforma en los **12 meses** anteriores (o cero si el uso fue gratuito), salvo dolo o culpa grave. Se excluyen daños indirectos y lucro cesante no imputables.

## 14. Indemnidad

Te comprometes a mantener indemne a CG CONSULTING frente a reclamaciones derivadas de tu uso indebido, violación de estos Términos o de derechos de terceros, o datos que ingreses sin base legal.

## 15. Ley aplicable y disputas

Ley de la República de Colombia. Controversias: negociación de buena fe y, si aplica, mecanismos previstos (incl. arbitraje ante la Cámara de Comercio de Bogotá cuando las partes así lo hayan pactado en contratos comerciales).

## 16. Contacto (completo — sin canales vacíos)

- **Correo:** ${C.email}  
- **WhatsApp:** ${C.whatsappDisplay} — ${WA_URL}  
- **Empresa:** ${C.company} — NIT ${C.nit} — ${C.address}

**RECONOCIMIENTO:** Al aceptar, confirmas que leíste estos Términos y la Política de Privacidad, y que autorizas el tratamiento de datos personales conforme a la Ley 1581 de 2012.`;

const TERMS_EN = `# Terms of Service

**Platform:** Ecosistema Fit / D28D  
**Last updated:** August 13, 2026  
**Operator:** ${C.company}, NIT ${C.nit}

## 1. Acceptance

By creating an account, signing in or using the platform you accept these Terms and the Privacy Policy. If you disagree, do not use the service.

## 2. Service description

Ecosistema Fit / D28D may include, depending on your plan and tenant: live classes / calendar / attendance; Training; Food Plan; Viento Recio; multi-tenant gym/sede/coach administration; payments and subscriptions. Your gym or coach may operate under white-label branding.

## 3. Eligibility

**18+** (or minors with authorization/supervision when allowed); legal capacity; accurate information.

## 4. Account security

You are responsible for credentials and activity under your account. Do not share access. Report unauthorized use to **${C.email}** or WhatsApp **${C.whatsappDisplay}**.

## 5. Acceptable use

Use the service lawfully. Prohibited: unauthorized access, abusive scraping, security interference, impersonation, harassment, illegal content, reverse engineering for unfair competition.

## 6. Intellectual property

Platform software, designs and marks belong to **${C.company}** or its licensors. You receive a limited, **non-exclusive**, revocable, personal license according to your plan. You keep ownership of content you upload, granting us a license to host and display it for the service.

## 7. Payments

Prices and methods (Wompi, on-site payment, etc.) depend on your product/tenant. Non-payment may limit access. In-person billing between you and your gym is between those parties.

## 8. Health disclaimer

Fitness, nutrition or spiritual information is **educational guidance**, not medical advice. Consult a health professional for relevant conditions. Use at your own risk.

## 9. Artificial intelligence

Some features use AI providers (e.g. Groq). Outputs may be imperfect and do not replace professional judgment. See the Privacy Policy.

## 10. Third-party services

Zoom, Wompi, WhatsApp, email and others have their own terms. Continuous availability is not controlled by us.

## 11. Availability and support

Service is provided “as is,” with reasonable efforts for uptime and Colombian business-hours support. Uninterrupted uptime is not guaranteed. Support: **${C.email}** / WhatsApp **${C.whatsappDisplay}**.

## 12. Suspension and termination

We may suspend/terminate for breach, fraud, security risk or legal requirement. Account closure and data retention follow the Privacy Policy (**30-day** transition).

## 13. Limitation of liability

To the maximum extent permitted by Colombian law, aggregate liability shall not exceed amounts you paid to the platform in the prior **12 months** (or zero if free), except willful misconduct or gross negligence. Indirect damages and lost profits not attributable to us are excluded.

## 14. Indemnity

You will indemnify CG CONSULTING against claims arising from your misuse, breach of these Terms, third-party rights, or data you enter without lawful basis.

## 15. Governing law

Laws of Colombia. Disputes: good-faith negotiation and, where agreed, arbitration before the Bogotá Chamber of Commerce.

## 16. Contact (complete — no empty channels)

- **Email:** ${C.email}  
- **WhatsApp:** ${C.whatsappDisplay} — ${WA_URL}  
- **Company:** ${C.company} — NIT ${C.nit} — ${C.address}

**ACKNOWLEDGEMENT:** By accepting, you confirm you read these Terms and the Privacy Policy and authorize personal-data processing under Law 1581 of 2012 as applicable.`;

export function getPrivacyContent(lang) {
  return lang === 'en' ? PRIVACY_EN : PRIVACY_ES;
}

export function getTermsContent(lang) {
  return lang === 'en' ? TERMS_EN : TERMS_ES;
}
